import React, { useCallback, useEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Navbar,
  Nav,
  Button,
  Modal,
  Badge,
  Spinner,
} from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import {
  User,
  authService,
  friendshipService,
  loadUnreadNotificationCount,
  vipService,
  VipPricingPublic,
} from '../services/api';
import {
  formatBrl,
  VIP_DURATION_DAYS,
  VIP_ORIGINAL_BRL,
  VIP_PROMO_BRL,
  vipPromoPercentOffFallback,
} from '../constants/vipPricing';
import LanguageSelector from './LanguageSelector';
import NavbarBrandLogo from './NavbarBrandLogo';
import UserNavbarMenus from './UserNavbarMenus';
import NotificationBell from './NotificationBell';
import NavbarMusicToggle from './NavbarMusicToggle';
import PwaInstallButton from './PwaInstallButton';
import LoginPage from './LoginPage';
import FriendsList from './FriendsList';
import PendingRequests from './PendingRequests';
import VipCardPaymentBrick from './VipCardPaymentBrick';
import { getVipDaysRemaining } from '../utils/getVipDaysRemaining';
import './HomePage.css';
import './VipPage.css';

function isVipUser(u: User | null): boolean {
  if (!u) return false;
  const ut = u.userType;
  return ut === 'Vip' || ut === 'Premium' || ut === 2;
}

function isAdminUser(u: User | null): boolean {
  if (!u) return false;
  const ut = u.userType;
  return ut === 'Admin' || ut === 3;
}

const VipPage: React.FC = () => {
  const { t, i18n } = useSafeTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showFriendsList, setShowFriendsList] = useState(false);
  const [showPendingRequests, setShowPendingRequests] = useState(false);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [vipPricing, setVipPricing] = useState<VipPricingPublic | null>(null);
  const [vipPricingLoading, setVipPricingLoading] = useState(true);

  const effectivePricing: VipPricingPublic =
    vipPricing ?? {
      originalPriceBrl: VIP_ORIGINAL_BRL,
      promoPriceBrl: VIP_PROMO_BRL,
      durationDays: VIP_DURATION_DAYS,
      discountPercent: vipPromoPercentOffFallback(),
    };

  const loadUser = useCallback(async () => {
    setIsLoadingUser(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setUser(null);
        setUnreadNotificationCount(0);
        return;
      }
      const u = await authService.getProfile();
      setUser(u);
      setUnreadNotificationCount(await loadUnreadNotificationCount(u));
    } catch {
      localStorage.removeItem('authToken');
      setUser(null);
      setUnreadNotificationCount(0);
    } finally {
      setIsLoadingUser(false);
    }
  }, []);

  useEffect(() => {
    void loadUser();
  }, [loadUser]);

  useEffect(() => {
    let cancelled = false;
    setVipPricingLoading(true);
    vipService
      .getPublicPricing()
      .then((p) => {
        if (!cancelled) setVipPricing(p);
      })
      .catch(() => {
        if (!cancelled) setVipPricing(null);
      })
      .finally(() => {
        if (!cancelled) setVipPricingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (user) {
      void friendshipService
        .getPendingRequests()
        .then((r) => setPendingRequestsCount(r.length))
        .catch(() => {});
    }
  }, [user]);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setUser(null);
    setUnreadNotificationCount(0);
    window.location.href = '/';
  };

  if (isLoadingUser) {
    return (
      <div className="home-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('common.loading', 'Loading...')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page vip-page">
      <Navbar bg="white" expand="lg" className="home-navbar" collapseOnSelect>
        <Container fluid>
          <Navbar.Brand
            as={Link}
            to="/"
            className="navbar-brand d-flex align-items-center"
            aria-label="Quizter Club"
          >
            <NavbarBrandLogo />
            <span className="d-none d-lg-inline">Quizter Club</span>
          </Navbar.Brand>

          <div className="d-flex d-lg-none align-items-center ms-auto gap-1 gap-sm-2 navbar-mobile-toolbar">
            <PwaInstallButton className="flex-shrink-0" iconOnly />
            <LanguageSelector user={user} isInline={true} />
            <NavbarMusicToggle className="flex-shrink-0" />
            {user && (
              <NotificationBell
                className="d-lg-none"
                unreadCount={unreadNotificationCount}
                onUnreadCountChange={setUnreadNotificationCount}
              />
            )}
            {user ? (
              <Navbar.Toggle aria-controls="basic-navbar-nav" />
            ) : (
              <Button
                type="button"
                variant="outline-primary"
                size="sm"
                className="d-inline-flex align-items-center justify-content-center flex-shrink-0 home-navbar-login-icon"
                onClick={() => setShowLoginModal(true)}
                title={t('common.login', 'Login')}
                aria-label={t('common.login', 'Login')}
              >
                <i className="bi bi-box-arrow-in-right" aria-hidden />
              </Button>
            )}
          </div>
          <Navbar.Collapse id="basic-navbar-nav" className="justify-content-lg-end">
            <Nav className="flex-column flex-lg-row align-items-stretch align-items-lg-center w-100 justify-content-lg-end">
              <div className="d-none d-lg-flex align-items-center gap-2 me-lg-3">
                <PwaInstallButton />
                <LanguageSelector user={user} isInline={true} />
                {!user && <NavbarMusicToggle />}
              </div>
              {user ? (
                <UserNavbarMenus
                  user={user}
                  pendingRequestsCount={pendingRequestsCount}
                  unreadNotificationCount={unreadNotificationCount}
                  onUnreadNotificationCountChange={setUnreadNotificationCount}
                  onOpenFriends={() => setShowFriendsList(true)}
                  onOpenPending={() => setShowPendingRequests(true)}
                  onLogout={handleLogout}
                />
              ) : (
                <Nav.Item className="d-none d-lg-block">
                  <Button variant="outline-primary" onClick={() => setShowLoginModal(true)}>
                    <i className="bi bi-box-arrow-in-right me-2"></i>
                    {t('common.login', 'Login')}
                  </Button>
                </Nav.Item>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Container fluid className="home-content vip-page-content">
        <Row className="justify-content-center py-4">
          <Col xs={12} lg={10} xl={8}>
            <div className="vip-page-hero text-center mb-4">
              <span className="vip-page-diamond" aria-hidden>
                💎
              </span>
              <h1 className="vip-page-title">{t('vip.pageTitle', 'VIP')}</h1>
              <p className="vip-page-lead">{t('vip.pageLead', 'Unlock the full Quizter Club experience.')}</p>
            </div>

            <Card className="vip-section-card mb-4 border-0 shadow-sm vip-benefits-card">
              <Card.Body className="vip-benefits-card-body">
                <h2 className="h6 mb-3 text-center vip-benefits-heading">
                  <i className="bi bi-stars text-warning me-2" aria-hidden />
                  {t('vip.benefitsTitle', 'VIP benefits')}
                </h2>
                <ul className="list-unstyled vip-benefit-list mb-0">
                  <li className="vip-benefit-item">
                    <span className="vip-benefit-icon text-danger" aria-hidden>
                      <i className="bi bi-heart-pulse-fill" />
                    </span>
                    <div className="vip-benefit-text">
                      <strong>
                        {t('vip.benefitLivesTitle', 'Double lives & faster recharge')}
                      </strong>
                      <p className="text-muted small mb-0 vip-benefit-desc">
                        {t(
                          'vip.benefitLivesDesc',
                          'Twice the life capacity and half the wait between recoveries while your VIP is active.'
                        )}
                      </p>
                    </div>
                  </li>
                  <li className="vip-benefit-item">
                    <span className="vip-benefit-icon text-primary" aria-hidden>
                      <i className="bi bi-lightning-charge-fill" />
                    </span>
                    <div className="vip-benefit-text">
                      <strong>{t('vip.benefitXpTitle', 'Double base XP')}</strong>
                      <p className="text-muted small mb-0 vip-benefit-desc">
                        {t(
                          'vip.benefitXpDesc',
                          'Earn 2× the base XP from any game mode while VIP is active.'
                        )}
                      </p>
                    </div>
                  </li>
                  <li className="vip-benefit-item">
                    <span className="vip-benefit-icon text-success" aria-hidden>
                      <i className="bi bi-bag-fill" />
                    </span>
                    <div className="vip-benefit-text">
                      <strong>{t('vip.benefitInventoryTitle', 'Double inventory slots')}</strong>
                      <p className="text-muted small mb-0 vip-benefit-desc">
                        {t(
                          'vip.benefitInventoryDesc',
                          'Twice the inventory capacity — keep more potions and rewards while VIP is active.'
                        )}
                      </p>
                    </div>
                  </li>
                  <li className="vip-benefit-item">
                    <span className="vip-benefit-icon text-warning" aria-hidden>
                      <i className="bi bi-person-badge-fill" />
                    </span>
                    <div className="vip-benefit-text">
                      <strong>{t('vip.benefitNicknameTitle', 'Stand out everywhere')}</strong>
                      <p className="text-muted small mb-0 vip-benefit-desc">
                        {t(
                          'vip.benefitNicknameDesc',
                          'Your nickname is highlighted in leaderboards and in PvP rooms.'
                        )}
                      </p>
                    </div>
                  </li>
                </ul>
              </Card.Body>
            </Card>

            <Card className="vip-section-card mb-4 border-0 shadow-sm vip-pricing-card">
              <Card.Body className="p-4 p-md-5 text-center">
                <p className="vip-support-line fw-semibold mb-3">
                  {t('vip.supportLine', 'Support Quizter Club and get perks!')}
                </p>
                <Badge bg="danger" className="vip-launch-badge mb-3">
                  {t('vip.launchPromoBadge', 'Launch promo')}
                </Badge>
                <div className="d-flex flex-wrap align-items-center justify-content-center gap-2 mb-2">
                  <span className="vip-price-original text-muted text-decoration-line-through">
                    {formatBrl(effectivePricing.originalPriceBrl)}
                  </span>
                  <Badge bg="success" className="vip-percent-badge fs-6">
                    {i18n.t('vip.percentOffBadge', {
                      percent: effectivePricing.discountPercent,
                      defaultValue: '{{percent}}% OFF',
                    })}
                  </Badge>
                </div>
                <p className="vip-price-promo mb-1">{formatBrl(effectivePricing.promoPriceBrl)}</p>
                <p className="text-muted mb-0">
                  {i18n.t('vip.durationLine', {
                    days: effectivePricing.durationDays,
                    defaultValue: '{{days}} days of VIP in Quizter Club with all benefits included.',
                  })}
                </p>
                {vipPricingLoading && (
                  <p className="text-muted small mt-2 mb-0">
                    <Spinner animation="border" size="sm" className="me-2" />
                    {t('vip.syncingPrices', 'Updating prices…')}
                  </p>
                )}
              </Card.Body>
            </Card>

            <Card className="vip-section-card border-0 shadow-sm vip-payment-placeholder">
              <Card.Body className="p-4 p-md-5">
                <h2 className="h6 mb-2">
                  <i className="bi bi-credit-card me-2 text-secondary" aria-hidden />
                  {t('vip.paymentSectionTitle', 'Payment')}
                </h2>
                <p className="text-muted small mb-3">
                  {t(
                    'vip.paymentSectionHint',
                    'Secure card checkout with Mercado Pago (transparent checkout). You are charged the promotional price shown above.'
                  )}
                </p>
                {!user && (
                  <div className="text-center">
                    <p className="text-muted small mb-3">
                      {t('vip.paymentLoginHint', 'Log in to complete your VIP purchase.')}
                    </p>
                    <Button variant="primary" size="sm" onClick={() => setShowLoginModal(true)}>
                      {t('common.login', 'Login')}
                    </Button>
                  </div>
                )}
                {user && isVipUser(user) && (
                  <div>
                    <p className="text-success mb-0 small">
                      <i className="bi bi-check-circle-fill me-1" aria-hidden />
                      {t('vip.alreadyVip', 'You already have VIP access.')}
                    </p>
                    <p className="text-muted small mb-0 mt-2">
                      {(() => {
                        const days = getVipDaysRemaining(user.vipExpiresAtUtc);
                        if (days === null) {
                          return t('vip.daysRemainingUnknown', 'Days remaining: —');
                        }
                        return i18n.t('vip.daysRemaining', { count: days });
                      })()}
                    </p>
                  </div>
                )}
                {user && isAdminUser(user) && (
                  <p className="text-muted mb-0 small">{t('vip.adminNoPurchase', 'Admin accounts do not need to purchase VIP.')}</p>
                )}
                {user && !isVipUser(user) && !isAdminUser(user) && (
                  <VipCardPaymentBrick
                    amount={effectivePricing.promoPriceBrl}
                    payerEmail={user.email}
                    onSuccess={() => loadUser()}
                  />
                )}
              </Card.Body>
            </Card>

            <Card className="profile-card mt-4 back-to-home-card">
              <Card.Body className="d-flex justify-content-center py-3">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  type="button"
                  onClick={() => navigate('/')}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  {t('navigation.backToHome', 'Back to Home')}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal show={showLoginModal} onHide={() => setShowLoginModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('auth.loginRequired', 'Login Required')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <LoginPage
            onLoginSuccess={() => {
              setShowLoginModal(false);
              void loadUser();
            }}
          />
        </Modal.Body>
      </Modal>

      {showFriendsList && <FriendsList onClose={() => setShowFriendsList(false)} />}
      {showPendingRequests && (
        <PendingRequests
          onClose={() => setShowPendingRequests(false)}
          onRequestResponded={() => {
            void friendshipService.getPendingRequests().then((r) => setPendingRequestsCount(r.length));
          }}
        />
      )}
    </div>
  );
};

export default VipPage;
